import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, ButtonGroup, Form, Card, Spinner, Container, Alert, Row, Col } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { faTrash, faPlus, faFile, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    createArchiveInstance,
    createDefaultFile,
    normalizeFileOrders,
    sortFilesByOrder,
    getFileDisplayName,
    DEFAULT_ARCHIVE_STYLE,
    DEFAULT_ARCHIVE_TITLE_STYLE,
    DEFAULT_ARCHIVE_FILE_STYLE,
} from '../../../Models/Archive';
import './ArchiveBuilder.css';

const ColorField = ({ label, color, show, onToggle, onChange }) => (
    <div className="mb-3">
        <label className="form-label">{label}</label>
        <div className="d-flex align-items-center">
            <div
                style={{
                    width: 30,
                    height: 30,
                    backgroundColor: color,
                    border: `1px solid ${color}`,
                    marginRight: 10,
                    cursor: 'pointer',
                }}
                onClick={onToggle}
            />
            <span>{color}</span>
        </div>
        {show && (
            <div style={{ position: 'absolute', zIndex: 2 }}>
                <div style={{ position: 'fixed', inset: 0 }} onClick={onToggle} />
                <SketchPicker color={color} onChange={onChange} />
            </div>
        )}
    </div>
);

const ArchiveBuilder = ({ id }) => {
    const [instance] = useState(createArchiveInstance());
    const [loading, setLoading] = useState(false);
    const [defaultMessage, setDefaultMessage] = useState(false);

    const [sectionTitle, setSectionTitle] = useState('');
    const [files, setFiles] = useState([]);
    const [currentStyle, setCurrentStyle] = useState({});

    const [showSectionBgPicker, setShowSectionBgPicker] = useState(false);
    const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
    const [showFileBgPicker, setShowFileBgPicker] = useState(false);
    const [showFileTitlePicker, setShowFileTitlePicker] = useState(false);
    const [showFileBorderPicker, setShowFileBorderPicker] = useState(false);

    const [fileMediaTarget, setFileMediaTarget] = useState(null);

    useEffect(() => {
        getData();
    }, []);

    const syncStyle = (updated) => {
        setCurrentStyle(updated);
        instance.setStyle(updated);
    };

    const getData = async () => {
        try {
            setLoading(true);
            if (id) {
                const data = await instance.load(id);
                if (Object.keys(data).length === 0) setDefaultMessage(true);
            } else {
                await instance.load();
            }
            if (Object.keys(instance.getData()).length > 0) {
                setSectionTitle(instance.getSectionTitle());
                setFiles(instance.getFiles());
                setCurrentStyle(instance.getStyle());
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load archive');
        } finally {
            setLoading(false);
        }
    };

    const updateFiles = (next) => {
        const normalized = normalizeFileOrders(next);
        setFiles(normalized);
        instance.setFiles(normalized);
    };

    const sortedFiles = sortFilesByOrder(files);

    const addFile = () => {
        const nextOrder = sortedFiles.length
            ? Math.max(...sortedFiles.map((f) => Number(f.order) || 0)) + 1
            : 1;
        updateFiles([...sortedFiles, createDefaultFile(Date.now(), nextOrder)]);
    };

    const moveFile = (fileId, direction, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        const sorted = sortFilesByOrder(files);
        const index = sorted.findIndex((f) => String(f.id) === String(fileId));
        if (index < 0) return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= sorted.length) return;
        const next = [...sorted];
        [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
        updateFiles(next);
    };

    const removeFile = (fileId) => {
        if (files.length <= 1) {
            toast.warning('At least one file entry is required');
            return;
        }
        updateFiles(files.filter((f) => f.id !== fileId));
    };

    const updateFileField = (fileId, field, value) => {
        updateFiles(files.map((f) => (f.id === fileId ? { ...f, [field]: value } : f)));
    };

    const handleFileSelect = (path) => {
        if (!fileMediaTarget) return;
        const name = path ? getFileDisplayName({ fileUrl: path }) : '';
        updateFiles(
            files.map((f) =>
                f.id === fileMediaTarget
                    ? { ...f, fileUrl: path || '', fileName: name }
                    : f
            )
        );
        setFileMediaTarget(null);
    };

    const handleSaveChanges = async () => {
        try {
            setLoading(true);
            instance.setSectionTitle(sectionTitle);
            instance.setFiles(files);
            await instance.save();
            toast.success('Changes saved successfully!');
            await getData();
        } catch (error) {
            toast.error('Failed to save changes. Please try again.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !files.length) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    const fileRowStyle = { ...DEFAULT_ARCHIVE_FILE_STYLE, ...currentStyle.ArchiveFile };

    return (
        <div className="container mt-4">
            <ToastContainer />
            {defaultMessage && (
                <Alert variant="info" className="mt-3" dismissible>
                    Default data has been loaded. Happy building!
                </Alert>
            )}
            <h3 className="mb-3">Archive Builder</h3>

            <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Content</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group className="mb-4">
                            <Form.Label>Section Title (optional)</Form.Label>
                            <Form.Control
                                type="text"
                                value={sectionTitle}
                                onChange={(e) => setSectionTitle(e.target.value)}
                                placeholder="e.g. Downloads & Resources"
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Files</h5>
                            <Button variant="outline-primary" size="sm" onClick={addFile}>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Add File
                            </Button>
                        </div>

                        <Accordion key={sortedFiles.map((f) => `${f.id}-${f.order}`).join('_')}>
                            {sortedFiles.map((file, index) => (
                                <Accordion.Item eventKey={`file-${file.id}`} key={`${file.id}-${file.order}`}>
                                    <div className="archive-file-header-row">
                                        {sortedFiles.length > 1 && (
                                            <div
                                                className="archive-file-order-btns"
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <ButtonGroup size="sm">
                                                    <Button
                                                        type="button"
                                                        variant="outline-secondary"
                                                        disabled={index === 0}
                                                        onClick={(e) => moveFile(file.id, 'up', e)}
                                                        title="Move up"
                                                    >
                                                        <FontAwesomeIcon icon={faArrowUp} />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline-secondary"
                                                        disabled={index === sortedFiles.length - 1}
                                                        onClick={(e) => moveFile(file.id, 'down', e)}
                                                        title="Move down"
                                                    >
                                                        <FontAwesomeIcon icon={faArrowDown} />
                                                    </Button>
                                                </ButtonGroup>
                                            </div>
                                        )}
                                        <Accordion.Header className="archive-file-accordion-header">
                                            <span className="me-2 text-muted small">#{file.order ?? index + 1}</span>
                                            {file.title?.trim() || getFileDisplayName(file) || `File ${index + 1}`}
                                        </Accordion.Header>
                                    </div>
                                    <Accordion.Body>
                                        <Form.Group className="mb-3">
                                            <Form.Label>File Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={file.title}
                                                onChange={(e) => updateFileField(file.id, 'title', e.target.value)}
                                                placeholder="e.g. Annual Report 2024"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>File</Form.Label>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <FontAwesomeIcon icon={faFile} className="text-muted" />
                                                {file.fileUrl ? (
                                                    <span className="small text-truncate" style={{ maxWidth: 320 }}>
                                                        {getFileDisplayName(file)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small">No file selected</span>
                                                )}
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => setFileMediaTarget(file.id)}
                                                >
                                                    Select File
                                                </Button>
                                                {file.fileUrl && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() =>
                                                            updateFiles(
                                                                files.map((f) =>
                                                                    f.id === file.id
                                                                        ? { ...f, fileUrl: '', fileName: '' }
                                                                        : f
                                                                )
                                                            )
                                                        }
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </div>
                                        </Form.Group>
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => removeFile(file.id)}
                                                disabled={files.length <= 1}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                Remove
                                            </Button>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Style</Accordion.Header>
                    <Accordion.Body>
                        <ColorField
                            label="Section Background Color"
                            color={currentStyle.Archive?.backgroundColor || '#ffffff'}
                            show={showSectionBgPicker}
                            onToggle={() => setShowSectionBgPicker(!showSectionBgPicker)}
                            onChange={(c) =>
                                syncStyle({
                                    ...currentStyle,
                                    Archive: { ...currentStyle.Archive, backgroundColor: c.hex },
                                })
                            }
                        />
                        <ColorField
                            label="Section Title Color"
                            color={currentStyle.ArchiveTitle?.color || '#212529'}
                            show={showTitleColorPicker}
                            onToggle={() => setShowTitleColorPicker(!showTitleColorPicker)}
                            onChange={(c) =>
                                syncStyle({
                                    ...currentStyle,
                                    ArchiveTitle: { color: c.hex },
                                })
                            }
                        />
                        <hr />
                        <h6 className="mb-3">File row style</h6>
                        <Row>
                            <Col md={6}>
                                <ColorField
                                    label="Row Background"
                                    color={fileRowStyle.backgroundColor}
                                    show={showFileBgPicker}
                                    onToggle={() => setShowFileBgPicker(!showFileBgPicker)}
                                    onChange={(c) =>
                                        syncStyle({
                                            ...currentStyle,
                                            ArchiveFile: {
                                                ...fileRowStyle,
                                                backgroundColor: c.hex,
                                            },
                                        })
                                    }
                                />
                            </Col>
                            <Col md={6}>
                                <ColorField
                                    label="File Title Color"
                                    color={fileRowStyle.titleColor}
                                    show={showFileTitlePicker}
                                    onToggle={() => setShowFileTitlePicker(!showFileTitlePicker)}
                                    onChange={(c) =>
                                        syncStyle({
                                            ...currentStyle,
                                            ArchiveFile: {
                                                ...fileRowStyle,
                                                titleColor: c.hex,
                                            },
                                        })
                                    }
                                />
                            </Col>
                        </Row>
                        <ColorField
                            label="Row Border Color"
                            color={fileRowStyle.borderColor}
                            show={showFileBorderPicker}
                            onToggle={() => setShowFileBorderPicker(!showFileBorderPicker)}
                            onChange={(c) =>
                                syncStyle({
                                    ...currentStyle,
                                    ArchiveFile: {
                                        ...fileRowStyle,
                                        borderColor: c.hex,
                                    },
                                })
                            }
                        />
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="d-flex justify-content-end mt-3">
                <Button variant="primary" onClick={handleSaveChanges} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>

            <MediaSelector
                isOpen={!!fileMediaTarget}
                onClose={() => setFileMediaTarget(null)}
                onSelect={handleFileSelect}
                isMultiple={false}
            />
        </div>
    );
};

export default ArchiveBuilder;
